import { UIAnalyticsEventInterface } from '@atlaskit/analytics-next';
import AnalyticsWebClient, {
  apdexType,
  envType,
  originType,
  tenantType,
  userType,
  // @ts-ignore Just ignore this one. Too hard to define a custom typing
} from '@atlassiansox/analytics-web-client';
import Cookies from 'js-cookie';
import memoize from 'memoize-one';

import { getConfig, getEnv } from '../config';
import { referrerHostName } from '../facade/window';
import { PageContent } from '../model/PageContent';
import UserStore from '../store/userStore';

const WAC_FIELDS = [
  '__aid_user_id',
  'ajs_anonymous_id',
  'ajs_user_id',
  'ajs_group_id',
  'atlCohort',
  'optimizelyEndUserId',
  'seg_xid',
];

type WAC_ANALYTICS_CONTEXT = Partial<{
  __aid_user_id: string;
  ajs_anonymous_id: string;
  ajs_group_id: string;
  ajs_user_id: string;
  atlCohort: string;
  optimizelyEndUserId: string;
  seg_xid: string;
}>;

const getAnalyticsContext = memoize(() => {
  const attributes = WAC_FIELDS.reduce<WAC_ANALYTICS_CONTEXT>((curr, next) => {
    curr[next as keyof WAC_ANALYTICS_CONTEXT] = Cookies.get(next);
    return curr;
  }, {});

  return attributes;
});

const { LOCAL, DEV, STAGING, PROD } = envType;
const ENV_MAP: { [key: string]: object } = {
  ddev: DEV,
  local: LOCAL,
  'stg-east': STAGING,
  test: LOCAL,
};
const getEnvType = () => ENV_MAP[getEnv()] || PROD;

let contentLoadTime: { [key: string]: number } = {};

export default interface AnalyticsClient {
  getAnalyticsWebClient(): AnalyticsWebClient;
  markContentLoad(pageContent: PageContent, isEmptyState: boolean): void;
  page(name: string, properties?: object): Promise<void>;
  sendEventFromAnalyticsListener(event: UIAnalyticsEventInterface, tag?: string): Promise<void>;
  startUIViewedEvent(): Promise<void>;
  track(subject: string, action: string, attributes?: object): Promise<void>;
  ui(subject: string, action: string, attributes?: object, additionalPayload?: object): Promise<void>;
}

export class AnalyticsClientImpl implements AnalyticsClient {
  private analyticsWebClient: AnalyticsWebClient;
  private readyPromise: Promise<void>;
  private userStore: UserStore;

  constructor(userStore: UserStore, subproduct = 'web') {
    this.userStore = userStore;
    this.analyticsWebClient = new AnalyticsWebClient(
      {
        env: getEnvType(),
        origin: originType.WEB,
        product: 'uchi',
        subproduct,
      },
      {
        apiHost: getConfig().gasApiHost,
      },
    );
    this.readyPromise = this.initialiseAnalytics();
  }

  getAnalyticsWebClient() {
    return this.analyticsWebClient;
  }

  /**
   * Signifies when start.atlassian.com has finished "loading"
   *
   * Start time: When user receives index.html
   * End time: When all clickable items are rendered (either empty state or both products and activity)
   */
  markContentLoad(pageContent: PageContent, isEmptyState: boolean) {
    // Ignore if start time wasn't recorded
    if (!window.__UCHI_PAGELOAD_START_TIME__) {
      return;
    }

    contentLoadTime[pageContent] = Date.now();
    this.analyticsWebClient.stopApdexEvent({
      additionalAttributes: {
        isEmptyState,
      },
      task: pageContent,
      type: apdexType.INITIAL_LOAD,
    });

    if (contentLoadTime[PageContent.ACTIVITY] && contentLoadTime[PageContent.PRODUCTS]) {
      this.analyticsWebClient.stopApdexEvent({
        additionalAttributes: {
          lastLoaded: pageContent,
          referrer: referrerHostName(),
        },
        task: 'pageload',
        type: apdexType.INITIAL_LOAD,
      });

      // Get rid of start time, since we're no longer on the initial load
      delete window.__UCHI_PAGELOAD_START_TIME__;
      contentLoadTime = {};
    }
  }

  async page(name: string, properties = {}) {
    await this.readyPromise;
    await new Promise(resolve => {
      this.analyticsWebClient.sendScreenEvent(
        {
          attributes: {
            ...properties,
            ...getAnalyticsContext(),
            referrer: referrerHostName(),
          },
          name,
        },
        resolve,
      );
    });
  }

  async sendEventFromAnalyticsListener(event: UIAnalyticsEventInterface, tag?: string) {
    const payload = event.payload || {};
    const { action, actionSubject, actionSubjectId, eventType, attributes } = payload;

    // Only track UI events. We don't really care about the other types
    if (eventType === 'ui') {
      return this.ui(actionSubject, action, attributes, {
        actionSubjectId,
        tags: [tag],
      });
    }
  }

  /**
   *  Start up E-MAU events in the new analytics pipeline (GAS v3)
   *  See: https://hello.atlassian.net/wiki/spaces/MAU/pages/127745766/DACI+E-MAU+Definition
   */
  async startUIViewedEvent() {
    await this.readyPromise;
    this.analyticsWebClient.startUIViewedEvent();
  }

  async track(subject: string, action: string, attributes = {}) {
    await this.readyPromise;
    await new Promise(resolve => {
      this.analyticsWebClient.sendTrackEvent(
        {
          action,
          actionSubject: subject,
          attributes: {
            ...attributes,
            referrer: referrerHostName(),
            ...getAnalyticsContext(),
          },
          source: this.getCurrentScreen(),
        },
        resolve,
      );
    });
  }

  async ui(subject: string, action: string, attributes = {}, additionalPayload: object = {}) {
    await this.readyPromise;
    await new Promise(resolve => {
      const params: { [key: string]: string | object } = {
        action,
        actionSubject: subject,
        attributes: {
          ...attributes,
          referrer: referrerHostName(),
          ...getAnalyticsContext(),
        },
        source: this.getCurrentScreen(),
        ...additionalPayload,
      };

      this.analyticsWebClient.sendUIEvent(params, resolve);
    });
  }

  private getCurrentScreen(): string {
    return 'home';
  }

  private async getUserAccountId(): Promise<string> {
    try {
      return (await this.userStore.readyPromise) || 'default';
    } catch (e) {
      return 'default';
    }
  }

  private async initialiseAnalytics(): Promise<void> {
    const accountId = await this.getUserAccountId();

    this.analyticsWebClient.setTenantInfo(tenantType.NONE);
    this.analyticsWebClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, accountId);
  }
}
